import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import { toast } from 'sonner';
import {
  getTimeEntriesList,
  type InputType as ListInput,
} from '../endpoints/time-entries/list_GET.schema';
import {
  createTimeEntry,
  type InputType as CreateInput,
} from '../endpoints/time-entries/create_POST.schema';
import { deleteTimeEntry } from '../endpoints/time-entries/delete_POST.schema';
import { getTimeCategoriesList } from '../endpoints/time-categories/list_GET.schema';
import {
  createTimeCategory,
  type InputType as CreateCatInput,
} from '../endpoints/time-categories/create_POST.schema';
import { deleteTimeCategory } from '../endpoints/time-categories/delete_POST.schema';

export const DEFAULT_CATEGORIES = [
  { id: '_dev', name: 'Development', color: '#4a7cdb' },
  { id: '_design', name: 'Design', color: '#c76b8a' },
  { id: '_meeting', name: 'Meetings', color: '#c9a84c' },
  { id: '_research', name: 'Research', color: '#3a9a8a' },
  { id: '_break', name: 'Break', color: '#6aab6a' },
] as const;

export function useTimeEntries(sessionId?: string) {
  return useQuery({
    queryKey: ['time-entries', sessionId || 'all'],
    queryFn: () => getTimeEntriesList({ sessionId }),
    staleTime: 30_000,
  });
}

export function useTimeCategories() {
  return useQuery({
    queryKey: ['time-categories'],
    queryFn: () => getTimeCategoriesList(),
    staleTime: 60_000,
  });
}

export function useMergedCategories() {
  const { data } = useTimeCategories();
  const dbCats = data?.categories || [];
  const defaults = DEFAULT_CATEGORIES.filter((d) => !dbCats.some((c) => c.name === d.name));
  return [...defaults, ...dbCats];
}

export function useCreateTimeEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTimeEntry,
    onSuccess: (_data, variables) => {
      toast.success('Time entry saved');
      queryClient.invalidateQueries({ queryKey: ['time-entries', variables.sessionId] });
      queryClient.invalidateQueries({ queryKey: ['time-entries', 'all'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to save entry: ${error.message}`);
    },
  });
}

export function useDeleteTimeEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTimeEntry,
    onSuccess: () => {
      toast.success('Entry deleted');
      queryClient.invalidateQueries({ queryKey: ['time-entries'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete: ${error.message}`);
    },
  });
}

export function useCreateTimeCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTimeCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-categories'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to create category: ${error.message}`);
    },
  });
}

export function useDeleteTimeCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTimeCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-categories'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete category: ${error.message}`);
    },
  });
}
